import React, { useState, useEffect, useCallback, useRef } from 'react';

interface NavigationItem {
  id: string;
  label: string;
  icon?: string;
}

interface CVNavigationProps {
  className?: string;
}

const CVNavigation: React.FC<CVNavigationProps> = ({ className = '' }) => {
  const [activeSection, setActiveSection] = useState<string>('personal-info');
  const navRef = useRef<HTMLElement>(null);
  const navContainerRef = useRef<HTMLDivElement>(null);

  // Navigation items
  const navigationItems: NavigationItem[] = [
    { id: 'personal-info', label: 'Informasi Pribadi', icon: 'fas fa-user' },
    { id: 'education', label: 'Pendidikan', icon: 'fas fa-graduation-cap' },
    { id: 'experience', label: 'Pengalaman & Proyek', icon: 'fas fa-briefcase' },
    { id: 'skills', label: 'Keahlian & Minat', icon: 'fas fa-cogs' },
    { id: 'additional', label: 'Informasi Tambahan', icon: 'fas fa-plus-circle' },
    // { id: 'export', label: 'Unduh CV', icon: 'fas fa-download' }
  ];

  // Scroll navigation to show active item
  const scrollNavToActiveItem = useCallback((sectionId: string) => {
    const container = navContainerRef.current;
    if (!container) return;

    const activeButton = container.querySelector(`[data-section="${sectionId}"]`) as HTMLElement;
    if (!activeButton) return;

    // Use getBoundingClientRect for accurate positioning
    const buttonRect = activeButton.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    
    // Calculate the button's position relative to the container's scroll area
    const buttonRelativeLeft = buttonRect.left - containerRect.left + container.scrollLeft;
    const buttonWidth = buttonRect.width;
    
    // Center the button in the container
    const buttonCenter = buttonRelativeLeft + (buttonWidth / 2);
    const containerCenter = containerRect.width / 2;
    let targetScroll = buttonCenter - containerCenter;
    
    // Ensure we don't scroll beyond boundaries
    const maxScroll = container.scrollWidth - container.clientWidth;
    targetScroll = Math.max(0, Math.min(targetScroll, maxScroll));
    
    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  }, []);

  // Scroll to section
  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navHeight = 120;
      const elementPosition = element.offsetTop - navHeight;
      
      window.scrollTo({
        top: Math.max(0, elementPosition),
        behavior: 'smooth'
      });
    }
    
    // Also scroll navigation
    scrollNavToActiveItem(sectionId);
  }, [scrollNavToActiveItem]);

  // Section scroll detection
  useEffect(() => {
    const sectionIds = ['personal-info', 'education', 'experience', 'skills', 'additional', 'export'];

    const handleScroll = () => {
      const scrollY = window.scrollY + 200;
      
      for (let i = sectionIds.length - 1; i >= 0; i--) {
        const element = document.getElementById(sectionIds[i]);
        if (element && element.offsetTop <= scrollY) {
          if (sectionIds[i] !== activeSection) {
            setActiveSection(sectionIds[i]);
            scrollNavToActiveItem(sectionIds[i]);
          }
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [activeSection, scrollNavToActiveItem]);

  return (
    <nav 
      ref={navRef}
      className={`cv-navigation ${className}`}
    >
      <div 
        ref={navContainerRef}
        className="cv-navigation-container"
      >
        <ul className="cv-navigation-list">
          {navigationItems.map((item) => (
            <li key={item.id} className="cv-navigation-item">
              <button
                type="button"
                className={`cv-navigation-link ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => scrollToSection(item.id)}
                title={item.label}
                data-section={item.id}
              >
                <span className="cv-navigation-label">{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

export default CVNavigation;